import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    SolUseClassification,
    SolUseClassificationUpdated,
    SolUseClassificationOnServerCreated,
    selectSolUseClassificationsActionLoading,
    selectSolUseClassificationById,
    selectLastCreatedSolUseClassificationId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-sol-use-classification-edit',
    templateUrl: './sol-use-classification-edit.component.html',
    styleUrls: ['./sol-use-classification-edit.component.scss'],
})


export class SolUseClassificationEditComponent implements OnInit, OnDestroy {

    model: SolUseClassification;
    oldModel: SolUseClassification;

    solUseClassificationForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param layoutUtilsService: LayoutUtilsService
     * @param store: Store<AppState>
     * @param layoutConfigService: LayoutConfigService
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectSolUseClassificationsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectSolUseClassificationById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initSolUseClassification();
                    }
                });
            } else {
                this.model = new SolUseClassification();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initSolUseClassification();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Approaches To Values', page: `../default/admin-management/linked-tables/sol-use-classifications`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/sol-use-classifications/edit/${id}`
                    : `../default/admin-management/linked-tables/sol-use-classifications/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initSolUseClassification() {
        this.createForm();
    }


    createForm() {
        this.solUseClassificationForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('SOL_USE_CLASSIFICATION.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('SOL_USE_CLASSIFICATION.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.solUseClassificationForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.solUseClassificationForm.markAsPristine();
        this.solUseClassificationForm.markAsUntouched();
        this.solUseClassificationForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.solUseClassificationForm.controls;
        /** check form */
        if (this.solUseClassificationForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateSolUseClassification(this.model);
        } else {
            this.createSolUseClassification(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: SolUseClassificationModel
     */
    updateSolUseClassification(_model: SolUseClassification) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateSolUseClassification: Update<SolUseClassification> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new SolUseClassificationUpdated({
            partialSolUseClassification: updateSolUseClassification,
            solUseClassification: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: SolUseClassificationModel
     */
    createSolUseClassification(_model: SolUseClassification) {
        this.store.dispatch(new SolUseClassificationOnServerCreated({solUseClassification: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedSolUseClassificationId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model
            && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }



    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
